import * as jstBroadcast from "../../../shared/native-modules/tare/jstBroadcast";
import { openShareSheet } from "../../../shared/native-modules/tare/openShareSheet";

import { useEffect } from "react";
import { Platform } from "react-native";

/** Tracking parameter value for screenshot-triggered shares */
const SCREENSHOT_SHARE_FROM_PARAM =
  Platform.OS === "ios" ? "appshareios_screenshot" : "appsharedroid_screenshot";

interface ScreenshotShareData {
  shareUrl: string;
  jobTitle: string;
}

/**
 * Hook to handle screenshot sharing for ViewJob
 * Listens for Screenshot broadcast events and opens the share sheet with job details
 */
export function useScreenshotShare(shareData: ScreenshotShareData | null): void {
  const shareUrl = shareData?.shareUrl;
  const jobTitle = shareData?.jobTitle;

  useEffect(() => {
    let subscription: jstBroadcast.JstBroadcastSubscription | null = null;
    let isMounted = true;

    const setup = async () => {
      try {
        const sub = await jstBroadcast.subscribe("Screenshot", (event) => {
          if (event.screen !== "viewjob") {
            return;
          }

          if (!shareUrl || !jobTitle) {
            console.warn("[useScreenshotShare] Missing share data, skipping share sheet");
            return;
          }

          // Add from parameter to share URL to track screenshot sharing
          const urlWithTracking = addFromParameter(shareUrl, SCREENSHOT_SHARE_FROM_PARAM);

          openShareSheet(urlWithTracking, jobTitle, null).catch((error: Error) => {
            console.error("[useScreenshotShare] Failed to open share sheet:", error);
          });
        });

        if (isMounted) {
          subscription = sub;
          if (!subscription) {
            console.warn("[useScreenshotShare] Failed to create screenshot subscription");
          }
        } else {
          // Component unmounted during setup - clean up immediately
          sub?.remove().catch((error: Error) => {
            console.error(
              "[useScreenshotShare] Failed to cleanup subscription after unmount:",
              error,
            );
          });
        }
      } catch (error) {
        console.error("[useScreenshotShare] Failed to subscribe to Screenshot event:", error);
      }
    };

    void setup();

    return () => {
      isMounted = false;
      if (subscription) {
        subscription.remove().catch((error: Error) => {
          console.error("[useScreenshotShare] Failed to unsubscribe from Screenshot event:", error);
        });
      }
    };
  }, [shareUrl, jobTitle]);
}

/**
 * Adds or updates the 'from' query parameter in a URL
 */
function addFromParameter(url: string, fromValue: string): string {
  try {
    const urlObj = new URL(url);
    urlObj.searchParams.set("from", fromValue);
    return urlObj.toString();
  } catch (error) {
    console.error("[addFromParameter] Invalid URL:", url, error);
    return url;
  }
}
